<template>
    <div :class="$style.page">
        <header :class="$style.header">
            <a
                href="/"
                :class="$style.logo_link"
                aria-label="Playgama for kids home"
            >
                <Logo mode="default" />
            </a>
        </header>

        <main :class="$style.content">
            <section :class="$style.heading">
                <Typography
                    is="h1"
                    type="header"
                    size="s"
                    responsive
                >
                    Games for kids
                </Typography>
                <Typography
                    is="p"
                    type="paragraph"
                    size="xs"
                    :responsive="false"
                    :class="$style.subtitle"
                >
                    Discover fun, free games made just for kids! No downloads or sign-ups — simply choose a game and
                    start playing.
                </Typography>
            </section>

            <div
                :class="$style.grid"
                @click.capture="handleGameClick"
            >
                <GameCard
                    v-for="(game, index) in shelfGames"
                    :key="game.hru"
                    :game="game"
                    :data-interface-target="`game-number-${index + 1}`"
                    :to="{ path: '/' }"
                    type="rectangle"
                    responsive
                    withBadges
                    withScaleOnHover
                    :class="$style.card_override"
                    :autoplayVideoInViewport="index === 0"
                    v-bind="
                        index < 3
                            ? {
                                  preload: index === 0,
                                  lazy: index !== 0,
                              }
                            : {}
                    "
                />
            </div>

            <KidsFooter />
        </main>

        <div
            v-if="activeGame"
            :class="$style.player"
            role="dialog"
            aria-modal="true"
            :aria-label="activeGame.title"
        >
            <button
                type="button"
                :class="$style.close"
                aria-label="Close game"
                @click="closeGame"
            >
                <Icon
                    name="close"
                    alt="Close game"
                />
            </button>
            <iframe
                :src="activeGame.gameUrl"
                :title="activeGame.title"
                allow="autoplay; fullscreen"
                allowfullscreen
                referrerpolicy="no-referrer"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useHead } from '@unhead/vue'
import { computed, onUnmounted, ref, watch } from 'vue'

import GameCard from '@/components/GameCard/GameCard.vue'
import Icon from '@/components/Icon/Icon.vue'
import Logo from '@/components/Logo.vue'
import Typography from '@/components/Typography.vue'
import type { ShortGameType } from '@/types'

import KidsFooter from './components/KidsFooter.vue'
import { KIDS_GAMES, type KidsGame } from './kids-games'

const activeGame = ref<KidsGame>()
const shelfGames = computed<ShortGameType[]>(() =>
    KIDS_GAMES.map((game) => ({
        hru: game.hru,
        title: game.title,
        preview: game.previewUrl,
        source: game.source,
        videoId: game.videoId,
        videoUrl: game.videoUrl,
    })),
)

useHead({
    title: 'Playgama for kids',
    htmlAttrs: { lang: 'en' },
    meta: [
        { name: 'description', content: 'A collection of featured games from Playgama for kids.' },
        { name: 'robots', content: 'noindex, nofollow' },
    ],
    link: [{ rel: 'canonical', href: 'https://kids.playgama.com/' }],
})

function openGame(game: KidsGame) {
    activeGame.value = game
}

function handleGameClick(event: MouseEvent) {
    if (!(event.target instanceof Element) || !(event.currentTarget instanceof HTMLElement)) {
        return
    }

    const card = event.target.closest('a')
    const index = card ? Array.from(event.currentTarget.children).indexOf(card) : -1
    const game = shelfGames.value[index]
    if (!game) {
        return
    }

    event.preventDefault()
    const kidsGame = KIDS_GAMES.find((item) => item.hru === game.hru)
    if (kidsGame) {
        openGame(kidsGame)
    }
}

function closeGame() {
    activeGame.value = undefined
}

function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
        closeGame()
    }
}

watch(activeGame, (game) => {
    document.body.style.overflow = game ? 'hidden' : ''
    if (game) {
        document.addEventListener('keydown', handleKeydown)
    } else {
        document.removeEventListener('keydown', handleKeydown)
    }
})

onUnmounted(() => {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', handleKeydown)
})
</script>

<style module>
.page {
    min-height: 100svh;
    background: var(--carbon-100);
    color: var(--white);
}

.header,
.content {
    margin-inline: 16px;
}

.header {
    border-bottom: 1px solid var(--white-10);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    margin-top: 16px;
}

.logo_link {
    background: var(--purple-400);
    border-radius: 62px;
    display: inline-flex;
    align-items: center;
    height: 56px;
    padding: 0 20px;
    text-decoration: none;
}

.logo_link svg {
    width: 148px;
    height: 32px;
    color: var(--white);
}

.content {
    padding: 22px 0 64px;
}

.heading {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 4px 0 24px;
}

.subtitle {
    color: var(--white-60);
}

.grid {
    display: grid;
    grid-auto-flow: dense;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 8px;
    position: relative;
    row-gap: 12px;
}

.card_override:nth-child(-n + 1) {
    grid-column: span 2;
    grid-row: span 2;
}

@media (--desktop-small-plus) {
    .subtitle {
        font-size: var(--font-size-paragraph-m);
        line-height: var(--line-height-paragraph-m);
    }

    .header,
    .content {
        margin-inline: 80px;
    }
}

@media (--desktop-large-plus) {
    .subtitle {
        font-size: var(--font-size-paragraph-l);
        line-height: var(--line-height-paragraph-l);
    }
}

@media (width >= 640px) {
    .grid {
        grid-template-columns: repeat(12, 1fr);
    }

    .card_override {
        grid-column: span 3;
    }

    .card_override:nth-child(-n + 3) {
        grid-column: span 4;
        grid-row: span 2;
    }
}

@media (width >= 1400px) {
    .grid {
        grid-template-columns: repeat(6, 1fr);
    }

    .card_override {
        grid-column: span 1;
    }

    .card_override:nth-child(-n + 3) {
        grid-column: span 2;
        grid-row: span 2;
    }
}

.player {
    background: #11111b;
    inset: 0;
    position: fixed;
    z-index: 10;
}

.player iframe {
    border: 0;
    height: 100%;
    width: 100%;
}

.close {
    align-items: center;
    background: #fff;
    border: 0;
    border-radius: 50%;
    color: var(--carbon-100);
    cursor: pointer;
    display: flex;
    height: 40px;
    justify-content: center;
    padding: 0;
    position: absolute;
    right: 16px;
    top: 16px;
    width: 40px;
    z-index: 1;
}

.close svg {
    height: 20px;
    width: 20px;
}
</style>
